import { jwtDecode } from "jwt-decode";
import { refreshAccessToken } from "../functions/auth";

/**
 * Token Refresh Manager
 * Automatically refreshes JWT tokens when user is active and token is about to expire
 */

let activityListenersAttached = false;
let tokenRefreshInterval = null;
let lastActivityTime = Date.now();
let isRefreshing = false;

// Activity detection - track user interactions
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
const INACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes of inactivity
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // Refresh if token expires in next 5 minutes

/**
 * Update last activity time
 */
const updateActivity = () => {
  lastActivityTime = Date.now();
};

/**
 * Check if user is currently active
 */
const isUserActive = () => {
  const timeSinceLastActivity = Date.now() - lastActivityTime;
  return timeSinceLastActivity < INACTIVITY_THRESHOLD;
};

/**
 * Get token expiration time (in milliseconds)
 */
const getTokenExpiration = (token) => {
  try {
    const decoded = jwtDecode(token);
    if (decoded && decoded.exp) {
      return decoded.exp * 1000; // Convert to milliseconds
    }
  } catch (error) {
    console.error("Error decoding token:", error);
  }
  return null;
};

/**
 * Check if token needs refresh (expires within threshold)
 */
const shouldRefreshToken = () => {
  const userData = localStorage.getItem("userData");
  if (!userData) return false;

  try {
    const parsed = JSON.parse(userData);
    const accessToken = parsed?.access_token;

    if (!accessToken) return false;

    const expirationTime = getTokenExpiration(accessToken);
    if (!expirationTime) return false;

    const timeUntilExpiration = expirationTime - Date.now();
    
    // Refresh if token expires within threshold AND user is active
    return timeUntilExpiration > 0 && 
           timeUntilExpiration < TOKEN_REFRESH_THRESHOLD && 
           isUserActive();
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return false;
  }
};

/**
 * Attempt to refresh the token
 */
const attemptTokenRefresh = async () => {
  // Prevent multiple simultaneous refresh attempts
  if (isRefreshing) {
    console.log("⏳ Token refresh already in progress, skipping...");
    return;
  }

  if (!shouldRefreshToken()) {
    return;
  }

  isRefreshing = true;
  try {
    console.log("🔄 Refreshing access token...");
    await refreshAccessToken();
    console.log("✅ Token refreshed successfully");
  } catch (error) {
    console.warn("⚠️ Token refresh failed:", error.message);
    // Don't clear localStorage here - let the 401 handler do it
  } finally {
    isRefreshing = false;
  }
};

/**
 * Attach activity listeners to track user interaction
 */
const attachActivityListeners = () => {
  if (activityListenersAttached) return;

  ACTIVITY_EVENTS.forEach(event => {
    document.addEventListener(event, updateActivity, { passive: true });
  });

  activityListenersAttached = true;
  console.log("👆 Activity tracking enabled");
};

/**
 * Remove activity listeners
 */
const detachActivityListeners = () => {
  if (!activityListenersAttached) return;

  ACTIVITY_EVENTS.forEach(event => {
    document.removeEventListener(event, updateActivity);
  });

  activityListenersAttached = false;
  console.log("👆 Activity tracking disabled");
};

/**
 * Start token refresh monitoring
 */
export const startTokenRefresh = () => {
  // Check if user is logged in
  const userData = localStorage.getItem("userData");
  if (!userData) {
    console.log("ℹ️ No user session, skipping token refresh setup");
    return;
  }

  // Attach activity listeners
  attachActivityListeners();

  // Check token every 2 minutes
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
  }

  tokenRefreshInterval = setInterval(() => {
    attemptTokenRefresh();
  }, 2 * 60 * 1000); // Check every 2 minutes

  // Also check immediately
  attemptTokenRefresh();

  console.log("🔄 Token refresh monitoring started");
};

/**
 * Stop token refresh monitoring
 */
export const stopTokenRefresh = () => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
  detachActivityListeners();
  console.log("🛑 Token refresh monitoring stopped");
};

/**
 * Manually trigger token refresh (useful before important API calls)
 */
export const refreshTokenIfNeeded = async () => {
  if (shouldRefreshToken()) {
    await attemptTokenRefresh();
  }
};
